<template>
  <div ref="componentRef" class="flex min-h-0 flex-1 flex-col">
    <Table
      :selection="tableSelection"
      v-model:selected="selected"
      v-model:sort="sort"
      v-model:mobile-selecting="mobileSelecting"
      :row-ids="rowIds"
      class="flex min-h-0 flex-1 flex-col"
    >
      <Table.Head :class="!rows.length && 'pointer-events-none'">
        <Table.Header primary field="name" sortable>Name</Table.Header>
        <Table.Header field="timestamp" sortable class="w-[250px]">
          Dacr Timestamp
        </Table.Header>
        <Table.Header class="w-[200px]">Status</Table.Header>
      </Table.Head>

      <template v-if="loading && !rows.length">
        <Table.Row
          v-for="n in skeletonCount"
          :key="`skeleton-${n}`"
          :id="`skeleton-${n}`"
        >
          <template #media>
            <div class="flex w-9 items-center justify-center tablet-h:w-6">
              <Skeleton class="h-6 w-[17px]" />
            </div>
          </template>

          <Table.Cell primary>
            <Skeleton class="h-[13px] w-[85px]" />
          </Table.Cell>
          <Table.Cell class="w-[250px]">
            <Skeleton class="h-[13px] w-[85px]" />
          </Table.Cell>
          <Table.Cell class="w-[200px]">
            <Skeleton class="h-[13px] w-[85px]" />
          </Table.Cell>

          <template #mobile>
            <div class="flex w-full items-center pl-[10px] pr-[10px]">
              <div class="min-w-0 flex-1">
                <Skeleton class="mb-1 h-[13px] w-[85px]" />
                <Skeleton class="h-[13px] w-[150px]" />
              </div>
            </div>
          </template>
        </Table.Row>
      </template>

      <div
        v-else-if="!rows.length"
        class="grid min-h-0 flex-1 grid-rows-[45fr_auto_55fr] pb-[calc(env(safe-area-inset-bottom)+100px)] tablet-h:pb-0 mr-4 tablet-h:mr-0 tablet-h:ml-7"
      >
        <NoFiles class="row-start-2" />
      </div>

      <Table.Virtual
        v-else
        :rows="rows"
        :row-height="rowHeight"
        class="min-h-0 flex-1 pb-[calc(env(safe-area-inset-bottom)+100px)] tablet-h:pb-0"
        @row-click="
          (_event: MouseEvent, item: unknown) => onRowClick(item as Item)
        "
        @scroll-end="emit('scroll-end')"
      >
        <template #media-row="{ row }: { row: Item }">
          <div class="w-9 tablet-h:w-6" :class="dimClass(row)">
            <Thumbnail :type="thumbType(row)" :src="previewSrc(row)" />
          </div>
        </template>

        <template #row="{ row }: { row: Item }">
          <div
            class="contents"
            @contextmenu.prevent="handleRightClick($event, row)"
          >
            <Table.Cell primary :class="dimClass(row)">
              <div class="flex min-w-0 items-center">
                <span class="truncate" @click.stop="navigate(row)">
                  {{ row.name }}
                </span>
                <div class="ml-2 flex items-center gap-x-2">
                  <Tooltip
                    v-if="isCoOwnerItem(row)"
                    content="Co-owned copyright"
                    :arrow="false"
                  >
                    <div
                      class="flex h-[22px] min-w-[22px] items-center justify-center rounded-sm bg-gray-800/5"
                    >
                      <CoOwnerIcon class="size-[14px] text-gray-800/60" />
                    </div>
                  </Tooltip>
                  <Tooltip
                    v-if="isAuthorItem(row)"
                    content="Authored copyright"
                  >
                    <div
                      class="flex h-[22px] min-w-[22px] items-center justify-center rounded-sm bg-gray-800/5"
                    >
                      <AuthorIcon class="size-[14px] text-[#7B7C7E]" />
                    </div>
                  </Tooltip>
                </div>
              </div>
            </Table.Cell>

            <Table.Cell class="w-[250px]" :class="dimClass(row)">
              <span v-if="isProcessingFile(row)" class="italic opacity-50"
                >Processing</span
              >
              <span v-else-if="isInactive(row)" class="opacity-50">—</span>
              <div
                v-else
                class="relative flex items-center gap-x-2"
                :class="timestampClasses(row)"
                @click.stop="onTimestamp(row)"
              >
                <DacrMarkIcon
                  v-if="isFileItem(row)"
                  class="size-4 text-gray-800"
                />
                <span>{{ timestampLabel(row) }}</span>
                <div
                  v-if="hasMore(row)"
                  class="flex items-center gap-x-1 text-[12px] leading-[18px] font-medium text-gray-500"
                >
                  <span>+{{ remaining(row) }} more</span>
                  <ChevronUpIcon class="size-[10px] rotate-180 text-gray-500" />
                </div>
              </div>
            </Table.Cell>

            <Table.Cell class="w-[200px]" :class="dimClass(row)">—</Table.Cell>
          </div>
        </template>

        <template #mobile-row="{ row }: { row: Item }">
          <div
            class="flex w-full items-center pl-[10px] pr-[18px]"
            :class="dimClass(row)"
          >
            <div class="min-w-0 flex-1">
              <div class="flex items-center">
                <p class="truncate text-[14px] leading-[18px] text-gray-800">
                  {{ row.name }}
                </p>
                <div
                  v-if="isCoOwnerItem(row)"
                  class="ml-2 flex h-[22px] min-w-[22px] items-center justify-center rounded-sm bg-gray-800/5"
                >
                  <CoOwnerIcon class="size-[14px] text-gray-800/60" />
                </div>
                <div
                  v-if="isAuthorItem(row)"
                  class="ml-2 flex h-[22px] min-w-[22px] items-center justify-center rounded-sm bg-gray-800/5"
                >
                  <AuthorIcon class="size-[14px] text-[#7B7C7E]" />
                </div>
              </div>
              <div
                v-if="isFileItem(row)"
                class="mt-1 flex items-center gap-[6px] text-[13px] leading-[18px] text-gray-600"
              >
                <span v-if="isProcessingFile(row)" class="italic opacity-50">
                  Processing
                </span>
                <span v-else-if="isInactive(row)" class="opacity-50">—</span>
                <template v-else>
                  <DacrMarkIcon class="size-[14px] shrink-0 text-gray-600" />
                  <span class="truncate">{{ timestampLabel(row) }}</span>
                  <span
                    v-if="hasMore(row)"
                    class="text-[12px] text-gray-800"
                    @click.stop="onTimestamp(row)"
                  >
                    +{{ remaining(row) }} more
                  </span>
                </template>
              </div>
            </div>
            <button
              v-if="!mobileSelecting && !isProcessingFile(row)"
              type="button"
              class="ml-[8px] shrink-0 text-gray-800"
              @click.stop="onThreeDots(row)"
            >
              <MenuIcon class="size-[18px]" />
            </button>
          </div>
        </template>
      </Table.Virtual>
    </Table>

    <div
      v-if="contextMenuComponent"
      ref="contextMenuRef"
      :style="contextMenuStyles"
      class="fixed z-100 pointer-events-auto"
      data-keep-selection
    >
      <FileContextMenu
        v-if="isFileContextMenu && selectedFiles[0]"
        :file="selectedFiles[0]"
      />
      <MultipleFilesContextMenu
        v-else-if="isMultipleFilesContextMenu"
        :files="selectedFiles"
      />
      <FolderContextMenu
        v-else-if="isFolderContextMenu"
        :items="selectedItems"
      />
      <MixedItemsContextMenu
        v-else-if="isMixedItemsContextMenu"
        :items="selectedItems"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  Table,
  Thumbnail,
  Skeleton,
  Tooltip,
  type ThumbnailType,
  type TableSort,
} from '@dacr/library'
import {
  DacrMarkIcon,
  MenuIcon,
  ChevronUpIcon,
  CoOwnerIcon,
  AuthorIcon,
} from '@dacr/icons'
import {
  CopyrightStatus,
  FileRole,
  type Item,
} from '@/shared/graphql/router/graphql'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import {
  isFile as isFileItem,
  isFolder as isFolderItem,
} from '@/features/explorer/utils/itemGuards'
import { useDevice } from '@/shared/composables/useDevice'
import { openSheet, Sheets } from '@/shared/dialogs/sheets'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken } from '@/shared/utils/url'
import NoFiles from './NoFiles.vue'
import FileContextMenu from './FileContextMenu.vue'
import MultipleFilesContextMenu from './MultipleFilesContextMenu.vue'
import FolderContextMenu from './FolderContextMenu.vue'
import MixedItemsContextMenu from './MixedItemsContextMenu.vue'
import { useExplorerContextMenu } from '../composables/useExplorerContextMenu'

const props = defineProps<{
  items?: Item[]
  loading?: boolean
}>()

const emit = defineEmits<{
  'scroll-end': []
}>()

const selected = defineModel<string[]>('selected', { default: () => [] })
const sort = defineModel<TableSort | null>('sort', { default: null })
const mobileSelecting = defineModel<boolean>('mobileSelecting', {
  default: false,
})

const device = useDevice()
const route = useRoute()
const router = useRouter()

const rows = computed(() => props.items ?? [])

const tableSelection = computed(() =>
  rows.value.length ||
  props.loading ||
  device.isTabletHorizontal.value ||
  device.isDesktop.value
    ? 'multiple'
    : undefined
)

watch(
  () => rows.value.length,
  (rowCount) => {
    if (rowCount) return
    mobileSelecting.value = false
    selected.value = []
  }
)

const rowHeight = computed(() =>
  device.isDesktop.value || device.isTabletHorizontal.value ? 41 : 64
)

const skeletonCount = computed(() =>
  device.isMobile.value || device.isTabletVertical.value ? 4 : 7
)

const fileOf = (item: Item) => (isFileItem(item) ? item : null)

const registrationsOf = (item: Item) =>
  fileOf(item)?.copyright?.registrations ?? []

const isProcessingFile = (item: Item) => {
  const file = fileOf(item)
  return !!file && (!file.copyright?.id || registrationsOf(item).length === 0)
}

const rowIds = computed(() =>
  rows.value.filter((item) => !isProcessingFile(item)).map((item) => item.id)
)

const {
  contextMenuComponent,
  contextMenuRef,
  componentRef,
  contextMenuStyles,
  isFileContextMenu,
  isMultipleFilesContextMenu,
  isFolderContextMenu,
  isMixedItemsContextMenu,
  selectedItems,
  selectedFiles,
  handleRightClick,
} = useExplorerContextMenu({
  items: rows,
  selected,
  isProcessingFile,
})

const isInactive = (item: Item) =>
  fileOf(item)?.copyright?.status === CopyrightStatus.Inactive

const dimClass = (item: Item) => (isInactive(item) ? 'opacity-50' : '')

const isCoOwnerItem = (item: Item) => fileOf(item)?.role === FileRole.CoOwner

const isAuthorItem = (item: Item) => fileOf(item)?.role === FileRole.Author

const timestampLabel = (item: Item) =>
  formatTimestamp(registrationsOf(item)[0]?.timestamp) || '—'

const hasMore = (item: Item) => registrationsOf(item).length > 1

const remaining = (item: Item) => registrationsOf(item).length - 1

const timestampClasses = (item: Item) =>
  hasMore(item)
    ? {
        "after:hidden hover:after:block after:rounded-md cursor-pointer after:content-[''] after:w-[calc(100%+14px)] after:h-[30px] after:absolute after:top-[-6px] after:left-[-7px]":
          true,
        'after:bg-green-700/15': selected.value.includes(item.id),
        'after:bg-gray-800/8': !selected.value.includes(item.id),
      }
    : {}

const thumbType = (item: Item): ThumbnailType =>
  isFolderItem(item) ? 'folder' : fileTypeToThumbnail(fileOf(item)?.type)

const previewSrc = (item: Item) =>
  applyAuthorizationToken(fileOf(item)?.asset?.previewImageUrl, [
    { key: 'width', value: 36 },
  ])

const navigate = (item: Item) => {
  const file = fileOf(item)

  if (file) {
    if (isProcessingFile(item)) return
    if (file.copyright.status === CopyrightStatus.Inactive) return

    selected.value = []
    router.push(`/file/${file.copyright.id}`)
    return
  }

  selected.value = []
  router.push({
    name: 'FileExplorerFolderView',
    params: { path: [...(route.params.path || []), item.name] },
  })
}

const onRowClick = (item: Item) => {
  if (device.isDesktop.value || device.isTabletHorizontal.value) return
  if (mobileSelecting.value) return
  navigate(item)
}

const onTimestamp = (item: Item) => {
  if (!isFileItem(item)) return
  if (registrationsOf(item).length < 2) return

  openDialog(Dialog.Registrations, { props: { file: item } })
}

const onThreeDots = (item: Item) => {
  if (isProcessingFile(item)) return

  if (isFileItem(item)) {
    openSheet(Sheets.FileActions, { props: { file: item } })
  } else {
    openSheet(Sheets.FolderActions, { props: { folder: item } })
  }
}
</script>
